/**
 * Helpers shared by the internal tool (which previews uploaded help documents)
 * and the customer app (which shows them on Settings > Documentation).
 *
 * A document is an HTML fragment authored outside the TMS: a `<title>`, a
 * Google Fonts `<link>`, inline `<style>` and the content, with screenshots
 * embedded as `data:` images and no scripts. Documents link to each other by
 * file name (`href="ratcfg01-tariffs.html"`), and the library stores each one
 * under the slug that file name produces.
 */

/**
 * `sandbox` for the iframe a document renders in. Scripts stay blocked.
 * `allow-same-origin` lets the host page measure the content height and catch
 * link clicks, which it cannot do from outside a fully opaque frame; it grants
 * the document nothing, because a document that cannot run script cannot use
 * the origin it is given. Popups are allowed so external links open in a tab.
 */
export const DOCUMENTATION_IFRAME_SANDBOX =
  'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

const HTML_EXTENSION = /\.html?$/i;
const NON_SLUG_RUN = /[^a-z0-9]+/g;
const EDGE_HYPHENS = /^-+|-+$/g;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * The library slug for an uploaded file name, which is also how other
 * documents link to it: `ADMCFG05 Fuel Profiles.html` becomes
 * `admcfg05-fuel-profiles`.
 */
export const documentationSlugFromFileName = (fileName: string): string => {
  const baseName = fileName.split(/[/\\]/).pop() ?? fileName;
  return baseName
    .replace(HTML_EXTENSION, '')
    .toLowerCase()
    .replaceAll(NON_SLUG_RUN, '-')
    .replaceAll(EDGE_HYPHENS, '');
};

/**
 * The `srcdoc` for a document's iframe. The fragment is placed in a full page
 * that pins the light theme (the documents switch palettes on
 * `:root[data-theme]`, and the TMS has no dark mode) and opens links in a new
 * tab unless the host page intercepts them. A document that is itself a full
 * page still renders: the parser folds its `<html>`, `<head>` and `<body>`
 * into these.
 */
export const buildDocumentationSrcDoc = (html: string): string =>
  `<!doctype html><html data-theme="light"><head><meta charset="utf-8"><base target="_blank"></head><body>${html}</body></html>`;

export type DocumentationLink =
  | { kind: 'document'; slug: string; fragment: string | null }
  | { kind: 'fragment'; fragment: string }
  | { kind: 'external'; href: string };

/**
 * What a link inside a document points at: another document in the library
 * (by file name), a section of the same document, or somewhere outside.
 * Returns `null` for an empty or unrecognised relative link, which the host
 * should leave inert.
 */
export const classifyDocumentationHref = (
  href: string,
): DocumentationLink | null => {
  const trimmed = href.trim();
  if (trimmed === '' || trimmed === '#') {
    return null;
  }
  if (trimmed.startsWith('#')) {
    return { kind: 'fragment', fragment: decodeURIComponent(trimmed.slice(1)) };
  }
  if (HAS_SCHEME.test(trimmed) || trimmed.startsWith('//')) {
    return { kind: 'external', href: trimmed };
  }
  const [beforeFragment = '', fragment] = trimmed.split('#', 2);
  const path = beforeFragment.split('?', 1)[0] ?? '';
  if (!HTML_EXTENSION.test(path)) {
    return null;
  }
  const slug = documentationSlugFromFileName(path);
  if (slug === '') {
    return null;
  }
  return {
    kind: 'document',
    slug,
    fragment:
      fragment === undefined || fragment === ''
        ? null
        : decodeURIComponent(fragment),
  };
};
